import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizationContext } from '../contexts/OrganizationContext';
import { LogOut, Settings, HelpCircle, Plug, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface UserMenuProps {
  showName?: boolean;
}

export function UserMenu({ showName = false }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { currentUserMember } = useOrganizationContext();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Use avatar from our DB (member profile) first, fall back to Clerk image
  const avatarSrc =
    currentUserMember?.avatar ||
    user?.avatar ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name ?? 'User')}&background=06b6d4&color=000`;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    navigate('/login');
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-tour="user-menu"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="flex items-center gap-3 w-full hover:bg-sidebar-hover rounded-lg transition-colors"
      >
        <img
          src={avatarSrc}
          alt={user.name}
          className="w-8 h-8 rounded-full border-2 border-border"
        />
        {showName && (
          <div className="flex-1 text-left">
            <div className="text-sm font-medium text-foreground">{user.name}</div>
            <div className="text-xs text-muted-foreground">{user.email}</div>
          </div>
        )}
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-64 bg-popover border border-border rounded-lg shadow-xl overflow-hidden z-50">
          {/* User Info */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <img
                src={avatarSrc}
                alt={user.name}
                className="w-10 h-10 rounded-full border-2 border-border"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{user.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2" role="menu">
            <button
              role="menuitem"
              onClick={() => {
                navigate('/integrations');
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-sidebar-hover transition-colors"
            >
              <Plug className="w-4 h-4" />
              Integrations
            </button>
            <button
              role="menuitem"
              onClick={() => {
                navigate('/billing');
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-sidebar-hover transition-colors"
            >
              <CreditCard className="w-4 h-4" />
              Billing
            </button>
            <button
              role="menuitem"
              onClick={() => {
                navigate('/settings');
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-sidebar-hover transition-colors"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            <button
              role="menuitem"
              onClick={() => {
                navigate('/help');
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-sidebar-hover transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              Help & Support
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-border p-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
